/**
 * Reach → Leads — slice 70. The people Reach's ads brought in.
 *
 * Two calls, both parsed against `contracts/reach-leads.ts` by `apiRequest`,
 * so a lead that arrived with an unmasked address or a field the contract does
 * not know fails here rather than drawing it:
 *
 *   - `GET /reach/leads` — newest first, paged, optionally one ad's, with the
 *     per-ad rollup and the week's total beside the page;
 *   - `POST /reach/leads/import` — a Meta lead-export CSV, multipart under the
 *     field name `file` (the dataset upload's own convention), into the
 *     workspace's system "Reach leads" dataset through the normal import.
 *
 * Nothing here sends. A lead becomes somebody a flow can greet only through a
 * flow the person launches themselves.
 */
import {
  reachLeadsImportResponseSchema,
  reachLeadsResponseSchema,
  type ReachLeadsImportResponse,
  type ReachLeadsResponse,
} from '@wend/shared';

import { apiRequest } from './client';

export interface ReachLeadsParams {
  readonly page: number;
  readonly pageSize: number;
  /** One ad's leads only; omitted for every ad. */
  readonly adId?: string;
}

/** `GET /reach/leads`. */
export async function fetchReachLeads(params: ReachLeadsParams, signal?: AbortSignal): Promise<ReachLeadsResponse> {
  return apiRequest({
    path: '/reach/leads',
    query: { page: String(params.page), pageSize: String(params.pageSize), adId: params.adId },
    schema: reachLeadsResponseSchema,
    signal,
  });
}

/** `POST /reach/leads/import` — Meta's lead export, as a CSV. */
export async function importReachLeads(file: File): Promise<ReachLeadsImportResponse> {
  const form = new FormData();
  form.append('file', file, file.name);
  return apiRequest({ path: '/reach/leads/import', method: 'POST', body: form, schema: reachLeadsImportResponseSchema });
}
